import { GlobalTimeslots, OpeningModeEnum } from '@api/ringil-api.v2';
import { dayjsFromApiUtc, dayjsFromApiUtcInTz, formatDateFromDayjsWithLocale } from '@utils/dateWithTz';
import { isNotNullOrUndefined, isNullOrUndefined } from '@utils/utils';

/**
 * A location's timeslots split into the date and the time a column can show separately.
 *
 * `formatTimeslotInterval` gives the date range only, as one string. The day resolution here is the
 * same — `utcDay` when the slot has a timezone, `day` in Europe/Prague otherwise — so a date field
 * and the v2 route cell can never disagree about which day a shipment loads. Ported from the
 * RD-2957 workspace PoC.
 */

const openDays = (timeslots: GlobalTimeslots) =>
  isNullOrUndefined(timeslots) || isNullOrUndefined(timeslots.dayWithInterval) ? [] : timeslots.dayWithInterval.filter(day => day.openingMode !== OpeningModeEnum.CLOSED);

const formatDay = (day: { day: string; utcDay?: string }, timezoneId?: string): string =>
  isNotNullOrUndefined(timezoneId) && isNotNullOrUndefined(day.utcDay)
    ? formatDateFromDayjsWithLocale(dayjsFromApiUtc(day.utcDay))
    : formatDateFromDayjsWithLocale(dayjsFromApiUtcInTz(day.day, 'Europe/Prague'));

/** The day, or "first - last" when the window spans several — the same text as `formatTimeslotInterval`. */
export const timeslotDateRange = (timeslots: GlobalTimeslots): string => {
  const days = openDays(timeslots);
  if (days.length === 0) return '';
  const first = formatDay(days[0], timeslots.timezoneId);
  const last = formatDay(days[days.length - 1], timeslots.timezoneId);
  return `${first} - ${last}`;
};

/** Earliest start to latest end across the open days' intervals ("08:00 – 18:00"). */
export const timeslotTimeRange = (timeslots: GlobalTimeslots): string => {
  const intervals = openDays(timeslots).flatMap(day => day.intervals ?? []);
  const since = intervals
    .map(interval => interval.sinceHoursString)
    .filter(Boolean)
    .sort()[0];
  const till = intervals
    .map(interval => interval.tillHoursString)
    .filter(Boolean)
    .sort()
    .pop();
  if (!since && !till) return '';
  return since === till ? since : `${since ?? ''} – ${till ?? ''}`;
};
